/* ============================================
   Artemis Theme — Customizer UI Controller
   ============================================
   Per-panel controller. Each product panel in the
   Apparel Designer instantiates one of these via
   ArtemisCustomizer.create(rootEl, options).

   Uploads go directly to the Cloudflare Worker
   specified in `uploadServiceUrl`. The response URL
   is used as the image source and passed back to
   the orchestrator for cart properties.
   ============================================ */

(function () {
  'use strict';

  var MAX_UPLOAD_SIZE = 5 * 1024 * 1024;
  var ACCEPTED_TYPES = [
    'image/png',
    'image/jpeg',
    'image/svg+xml',
    'image/webp',
    'application/pdf',
    'application/postscript',
    'application/illustrator',
    'application/vnd.adobe.illustrator',
  ];

  /**
   * Create a customizer instance bound to `rootEl`.
   *
   * @param {HTMLElement} rootEl                  — container that holds data-customizer-* elements
   * @param {Object}      options
   * @param {Array}       options.locations       — print locations [{id,label,view,x,y,w,h}, ...]
   * @param {Object}      options.mockups         — { color: { view: url } }
   * @param {string}      options.uploadServiceUrl — base URL of the upload Worker (no trailing slash)
   * @param {string}     [options.initialColor]
   * @param {string}     [options.initialMode='same']
   * @param {Function}   [options.onChange]       — called with canvas state on every mutation
   * @returns {Object} controller { destroy, getState, setColor, rehydrate, canvas }
   */
  function create(rootEl, options) {
    options = options || {};
    if (!rootEl) return null;

    var canvasEl = rootEl.querySelector('[data-customizer-canvas]');
    if (!canvasEl) return null;

    var locations = options.locations || [];
    var mockups = options.mockups || {};
    var uploadServiceUrl = (options.uploadServiceUrl || '').replace(/\/+$/, '');

    if (!locations.length) return null;

    var canvas = new window.ArtemisCanvas(canvasEl, locations, mockups, options.initialColor);
    canvas.setMode(options.initialMode || 'same');

    if (locations.length > 0) canvas.setLocation(locations[0].id);

    /* ---- DOM lookups scoped to rootEl ---- */
    var uploadInput = rootEl.querySelector('[data-customizer-upload]');
    var uploadBtn = rootEl.querySelector('[data-customizer-action="upload"]');
    var uploadPreview = rootEl.querySelector('[data-customizer-upload-preview]');
    var uploadName = rootEl.querySelector('[data-customizer-upload-name]');
    var clearBtn = rootEl.querySelector('[data-customizer-action="clear"]');
    var locationBtns = rootEl.querySelectorAll('[data-customizer-location]');
    var viewTabs = rootEl.querySelectorAll('[data-customizer-view]');
    var modeRadios = rootEl.querySelectorAll('[data-customizer-mode]');
    var statusEl = rootEl.querySelector('[data-customizer-status]');
    var errorEl = rootEl.querySelector('[data-customizer-error]');
    var summaryEl = rootEl.querySelector('[data-canvas-summary]');

    /* ---- Canvas text alternative ----
       The canvas is a non-interactive preview. Keep its accessible name and an
       adjacent polite live region in sync with the current customization so AT
       users hear what changed (e.g. "Front view: Left Chest: shared logo").
    */
    function updateCanvasSummary() {
      var summary = canvas.getSummary();
      var view = canvas.activeView
        ? canvas.activeView.replace(/-/g, ' ').replace(/\b\w/g, function (c) { return c.toUpperCase(); })
        : '';
      var text = (view ? view + ' view. ' : '') + summary;
      var label = 'Product customization preview. ' + text;
      canvasEl.setAttribute('aria-label', label);
      if (summaryEl) summaryEl.textContent = text;
    }

    updateViewTabs();
    updateLocationButtons();
    updateModeUI();

    /* ---- Upload ---- */
    function showStatus(message, type) {
      // Errors go to the assertive (role="alert") region so AT interrupts;
      // everything else to the polite status region.
      if (type === 'error') {
        if (errorEl) errorEl.textContent = message;
        return;
      }
      if (errorEl) errorEl.textContent = '';
      if (!statusEl) return;
      statusEl.textContent = message;
      // Keep the visually-hidden live-region styling; just reflect the type
      // via a modifier without clobbering the base class.
      statusEl.classList.remove('customizer__status--info', 'customizer__status--success', 'customizer__status--error');
      if (type) statusEl.classList.add('customizer__status--' + type);
    }

    function showUploadPreview(filename, url) {
      if (uploadPreview) {
        uploadPreview.innerHTML = '<img src="' + url + '" alt="Logo preview" crossorigin="anonymous">';
        uploadPreview.style.display = 'block';
      }
      if (uploadName) {
        uploadName.textContent = filename || '';
        uploadName.style.display = filename ? 'block' : 'none';
      }
      if (clearBtn) clearBtn.style.display = '';
      if (uploadBtn) {
        var span = uploadBtn.querySelector('span');
        if (span) span.textContent = 'Change';
      }
    }

    function clearUploadPreview() {
      if (uploadPreview) {
        uploadPreview.innerHTML = '';
        uploadPreview.style.display = 'none';
      }
      if (uploadName) {
        uploadName.textContent = '';
        uploadName.style.display = 'none';
      }
      if (clearBtn) clearBtn.style.display = 'none';
      if (uploadBtn) {
        var span = uploadBtn.querySelector('span');
        if (span) span.textContent = 'Upload Logo';
      }
    }

    function validateFile(file) {
      if (ACCEPTED_TYPES.indexOf(file.type) < 0) {
        showStatus('Please upload a PNG, JPG, SVG, WebP, PDF, AI, or EPS file.', 'error');
        return false;
      }
      if (file.size > MAX_UPLOAD_SIZE) {
        showStatus('File must be under 5MB.', 'error');
        return false;
      }
      return true;
    }

    function uploadToR2(file) {
      if (!uploadServiceUrl) {
        return Promise.reject(new Error('Upload service not configured'));
      }
      return fetch(uploadServiceUrl + '/upload', {
        method: 'POST',
        headers: { 'Content-Type': file.type },
        body: file,
      }).then(function (res) {
        if (!res.ok) throw new Error('Upload failed (' + res.status + ')');
        return res.json();
      });
    }

    function loadImage(url) {
      return new Promise(function (resolve, reject) {
        var img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = function () { resolve(img); };
        img.onerror = function () { reject(new Error('Image failed to load')); };
        img.src = url;
      });
    }

    function handleFile(file) {
      if (!validateFile(file)) return;
      showStatus('Uploading…', '');
      uploadToR2(file)
        .then(function (resp) {
          var previewUrl = resp.previewUrl || resp.url;
          return loadImage(previewUrl).then(function (img) {
            return { img: img, url: resp.url, previewUrl: previewUrl, key: resp.key };
          });
        })
        .then(function (result) {
          if (canvas.mode === 'same') {
            canvas.setSharedLogo(result.img, result.url, result.key, result.previewUrl);
            // Ensure the active location is enabled when the user first uploads
            if (canvas.activeLocationId && !canvas.isLocationEnabled(canvas.activeLocationId)) {
              canvas.toggleLocationEnabled(canvas.activeLocationId);
            }
          } else {
            var activeId = canvas.activeLocationId || (locations[0] && locations[0].id);
            if (!activeId) {
              showStatus('Pick a location first.', 'error');
              return;
            }
            canvas.setPerSideArt(activeId, result.img, result.url, result.key, result.previewUrl);
          }
          showUploadPreview(file.name, result.previewUrl);
          showStatus('Logo applied.', 'success');
          updateLocationButtons();
        })
        .catch(function (err) {
          showStatus(err.message || 'Upload failed.', 'error');
        });
    }

    if (uploadBtn && uploadInput) {
      uploadBtn.addEventListener('click', function () { uploadInput.click(); });
      uploadInput.addEventListener('change', function () {
        var file = uploadInput.files[0];
        if (!file) return;
        handleFile(file);
        uploadInput.value = '';
      });
    }

    if (clearBtn) {
      clearBtn.addEventListener('click', function () {
        if (canvas.mode === 'same') {
          canvas.clearSharedLogo();
        } else if (canvas.activeLocationId) {
          canvas.clearPerSideArt(canvas.activeLocationId);
        }
        clearUploadPreview();
        showStatus('', '');
        refreshUploadPreviewForActiveLocation();
        updateLocationButtons();
      });
    }

    /* ---- Mode toggle ---- */
    modeRadios.forEach(function (input) {
      input.addEventListener('change', function () {
        if (!input.checked) return;
        var mode = input.getAttribute('data-customizer-mode');
        canvas.setMode(mode);
        updateModeUI();
        refreshUploadPreviewForActiveLocation();
        updateLocationButtons();
      });
    });

    function updateModeUI() {
      modeRadios.forEach(function (input) {
        input.checked = input.getAttribute('data-customizer-mode') === canvas.mode;
      });
      rootEl.setAttribute('data-mode', canvas.mode);
    }

    /* ---- Location buttons ----
       In 'same' mode: button click toggles whether the shared logo prints
       at that location. Active highlight still tracks the most-recently-clicked
       location (for the upload preview's context).
       In 'different' mode: button click selects which location's art we're
       editing; upload replaces art at that location.
    */
    locationBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var locId = btn.getAttribute('data-customizer-location');
        canvas.setLocation(locId);
        var loc = canvas.getActiveLocation();
        if (loc && loc.view !== canvas.activeView) {
          canvas.setView(loc.view);
          updateViewTabs();
        }
        if (canvas.mode === 'same' && canvas.sharedLogo) {
          canvas.toggleLocationEnabled(locId);
        }
        updateLocationButtons();
        refreshUploadPreviewForActiveLocation();
      });
    });

    function updateLocationButtons() {
      var activeId = canvas.activeLocationId;
      var enabledViews = {};
      locationBtns.forEach(function (btn) {
        var locId = btn.getAttribute('data-customizer-location');
        var view = btn.getAttribute('data-customizer-location-view') || '';
        // Hide a location button when it's off-view, or when the entry's
        // decoration method disallows it (data-customizer-location-allowed is
        // set by the designer orchestrator).
        var notAllowed = btn.getAttribute('data-customizer-location-allowed') === 'false';
        btn.hidden = notAllowed || !!(view && view !== canvas.activeView);
        // The name placement carries text, not a logo, and is enabled via a flag
        // the designer orchestrator sets. It still takes part in the per-view
        // single-selection rule: a back logo and the back name are mutually
        // exclusive (one decoration on the back), so it occupies its view too.
        var isNames = locId === 'name-back';
        var enabled = isNames
          ? btn.getAttribute('data-customizer-names-enabled') === 'true'
          : (canvas.mode === 'same'
              ? canvas.isLocationEnabled(locId)
              : !!canvas.perSideArt[locId]);
        btn.classList.toggle('customizer__location-btn--active', locId === activeId);
        btn.classList.toggle('customizer__location-btn--enabled', enabled);
        // Expose enabled/selected state to assistive tech (the class alone is
        // invisible to AT).
        btn.setAttribute('aria-pressed', enabled ? 'true' : 'false');
        if (enabled && view) enabledViews[view] = locId;
      });
      // Mark sibling buttons (same view, not currently enabled) as occupied so
      // customers understand selecting them would swap off the active sibling.
      // The name placement participates: it replaces, and is replaced by, the
      // back logo.
      locationBtns.forEach(function (btn) {
        var locId = btn.getAttribute('data-customizer-location');
        var view = btn.getAttribute('data-customizer-location-view') || '';
        var siblingOccupied = !!(view && enabledViews[view] && enabledViews[view] !== locId);
        btn.classList.toggle('customizer__location-btn--sibling-occupied', siblingOccupied);
      });
    }

    /* ---- View tabs ---- */
    viewTabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        var view = tab.getAttribute('data-customizer-view');
        canvas.setView(view);
        updateViewTabs();
        updateLocationButtons();
      });
    });

    function updateViewTabs() {
      viewTabs.forEach(function (tab) {
        var isActive = tab.getAttribute('data-customizer-view') === canvas.activeView;
        tab.classList.toggle('customizer__view-tab--active', isActive);
        tab.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
    }

    /* ---- Upload preview sync ----
       When the user switches mode or active location in 'different' mode,
       refresh the preview to reflect the art (or lack thereof) for that
       location.
    */
    function refreshUploadPreviewForActiveLocation() {
      if (canvas.mode === 'same') {
        if (canvas.sharedLogo) {
          showUploadPreview('', canvas.sharedLogo.url);
        } else {
          clearUploadPreview();
        }
        return;
      }
      var art = canvas.activeLocationId ? canvas.perSideArt[canvas.activeLocationId] : null;
      if (art) showUploadPreview('', art.url);
      else clearUploadPreview();
    }

    /* ---- State change callback ----
       Fires on every canvas mutation, including ones driven externally (the
       designer orchestrator painting per-view logos). Refresh the location
       buttons so their enabled / sibling-occupied state always tracks the
       canvas, then hand the state to the owner.
    */
    canvas.onStateChange = function () {
      updateLocationButtons();
      updateCanvasSummary();
      if (typeof options.onChange === 'function') options.onChange(canvas.getState());
    };

    // Seed the canvas description on init (before any state change fires).
    updateCanvasSummary();

    /* ---- Controller surface ---- */
    return {
      canvas: canvas,
      getState: function () { return canvas.getState(); },
      getSummary: function () { return canvas.getSummary(); },
      hasAnyArt: function () { return canvas.hasAnyArt(); },
      // Re-sync the view tabs + location buttons with the DOM/canvas — used
      // when the designer changes location allowance (decoration method).
      refresh: function () {
        updateViewTabs();
        updateLocationButtons();
      },
      setColor: function (color) {
        canvas.setColor(color);
        updateViewTabs();
        updateLocationButtons();
      },
      rehydrate: function (state) {
        if (!state) return;
        if (state.mode) canvas.setMode(state.mode);
        updateModeUI();
        if (state.mode === 'same' && state.sharedLogo && state.sharedLogo.url) {
          var sharedSrc = state.sharedLogo.previewUrl || state.sharedLogo.url;
          loadImage(sharedSrc).then(function (img) {
            canvas.setSharedLogo(img, state.sharedLogo.url, state.sharedLogo.key, sharedSrc);
            (state.sharedLogo.enabledLocationIds || []).forEach(function (id) {
              if (!canvas.isLocationEnabled(id)) canvas.toggleLocationEnabled(id);
            });
            showUploadPreview('', sharedSrc);
            updateLocationButtons();
          });
        } else if (state.mode === 'different' && state.perSideArt) {
          Object.keys(state.perSideArt).forEach(function (id) {
            var entry = state.perSideArt[id];
            // Prefer the digitized stitch (TrueView) render when one was saved,
            // so embroidery previews survive a reload without re-digitizing.
            var digitized = entry.digitized;
            var entrySrc = (digitized && digitized.status === 'ok' && digitized.previewUrl)
              ? digitized.previewUrl
              : (entry.previewUrl || entry.url);
            loadImage(entrySrc).then(function (img) {
              canvas.setPerSideArt(id, img, entry.url, entry.key, entrySrc);
              if (digitized && canvas.perSideArt[id]) canvas.perSideArt[id].digitized = digitized;
              updateLocationButtons();
              refreshUploadPreviewForActiveLocation();
            });
          });
        }
      },
      destroy: function () {
        canvas.destroy();
      },
    };
  }

  window.ArtemisCustomizer = { create: create };
})();
